import React, { useState, useEffect, useMemo, createContext, useContext } from 'react';
import PropTypes from 'prop-types';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creditLocked, setCreditLocked] = useState(false);
  const [passwordExpired, setPasswordExpired] = useState(false);
  const [twoFAPending, setTwoFAPending] = useState(null);

  const checkCreditStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const resp = await fetch(`${API_URL}/api/credits/check-status`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setCreditLocked(data.is_locked === true);
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const savedTenant = localStorage.getItem('tenant');
    if (!token || !savedUser) {
      setLoading(false);
      return undefined;
    }

    try {
      setUser(JSON.parse(savedUser));
      if (savedTenant) setTenant(JSON.parse(savedTenant));
    } catch (e) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('tenant');
      setLoading(false);
      return undefined;
    }

    // Refresh user (including module_access) so RBAC is not stuck on stale localStorage
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.ok) {
          const freshUser = await resp.json();
          if (!cancelled) {
            localStorage.setItem('user', JSON.stringify(freshUser));
            setUser(freshUser);
          }
        } else if (resp.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          localStorage.removeItem('tenant');
          if (!cancelled) {
            setUser(null);
            setTenant(null);
          }
        }
      } catch { /* keep cached user */ }
      finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (user && !user.is_system_admin) {
      checkCreditStatus();
      refreshTenant();
      const interval = setInterval(refreshTenant, 60 * 1000);
      const onFocus = () => refreshTenant();
      window.addEventListener('focus', onFocus);
      return () => {
        clearInterval(interval);
        window.removeEventListener('focus', onFocus);
      };
    }
  }, [user]);

  const login = async (email, password, captcha_token) => {
    const response = await fetch(`${API_URL}/api/auth/login/json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: typeof email === 'string' ? email.trim() : email,
        password: typeof password === 'string' ? password.trim() : password,
        captcha_token,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const detail = error.detail;
      let message = 'Login failed';
      if (typeof detail === 'string' && detail) message = detail;
      else if (Array.isArray(detail)) message = detail.map(e => e.msg || JSON.stringify(e)).join('; ');
      throw new Error(message);
    }

    const data = await response.json();

    // 2FA required — return partial response, don't set tokens yet
    if (data.requires_2fa) {
      setTwoFAPending({ twofa_token: data.twofa_token, email });
      return data;
    }

    _handleLoginSuccess(data);
    return data;
  };

  const verify2FA = async (twofa_token, otp, trust_device = false) => {
    const response = await fetch(`${API_URL}/api/auth/2fa/verify-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ twofa_token, otp, trust_device }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'OTP verification failed');
    }

    const data = await response.json();
    setTwoFAPending(null);
    _handleLoginSuccess(data);
    return data;
  };

  const cancelTwoFA = () => setTwoFAPending(null);

  const _handleLoginSuccess = (data) => {
    localStorage.setItem('token', data.access_token);
    if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
    localStorage.setItem('user', JSON.stringify(data.user));
    if (data.tenant) {
      localStorage.setItem('tenant', JSON.stringify(data.tenant));
      setTenant(data.tenant);
    } else {
      localStorage.removeItem('tenant');
      setTenant(null);
    }
    setUser(data.user);
    if (data.password_expired) {
      setPasswordExpired(true);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');
    setUser(null);
    setTenant(null);
    setCreditLocked(false);
    setPasswordExpired(false);
  };

  const updateUser = (updatedUser) => {
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  const updateTenant = (updatedTenant) => {
    if (updatedTenant) {
      localStorage.setItem('tenant', JSON.stringify(updatedTenant));
      setTenant(updatedTenant);
    }
  };

  const refreshTenant = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const resp = await fetch(`${API_URL}/api/tenant/profile`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        localStorage.setItem('tenant', JSON.stringify(data));
        setTenant(data);
      }
    } catch { /* ignore */ }
  };

  const contextValue = useMemo(() => ({
    user, tenant, loading, login, verify2FA, cancelTwoFA, twoFAPending, logout, isAuthenticated: !!user, creditLocked, passwordExpired, setPasswordExpired, updateUser, updateTenant, refreshTenant
  }), [user, tenant, loading, twoFAPending, creditLocked, passwordExpired]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
